import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { endpoints } from 'src/utils/axios-hire';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';
import { sendIdentify } from 'src/analytics/datatrack';
import { useRouter, useSearchParams } from 'src/routes/hooks';
import { PATH_AFTER_LOGIN } from 'src/config-global';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  user: null,
  organization: null,
  loading: true,
  verified:null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
      organization: action.payload.organization,
      verified: action.payload.verified,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
      organization: action.payload.organization,
      verified: action.payload.verified,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
      organization: action.payload.organization,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
      organization: null,
      authenticated: false,
      unauthenticated: true,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';
const USER_KEY = 'userKey';
const ORG_KEY = 'orgKey';

export function AuthProvider({ children }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);
      if (accessToken && isValidToken(accessToken)) {
        const userStr = localStorage.getItem(USER_KEY);
        const user = JSON.parse(userStr);
        const orgStr = localStorage.getItem(ORG_KEY);
        const organization = JSON.parse(orgStr);
        // console.log("USer Details:"+user);
        var verified=true;
        if(organization && organization.approval_status === 'PENDING_APPROVAL'){
          verified=false;
        }

        setSession(accessToken);
        sendIdentify(user);
        dispatch({
          type: 'INITIAL',
          payload: {
            user,
            organization,
            verified,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
            organization: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
          organization: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  //LOGIN 
  const login = useCallback(
    async (email, password, orgId) => {
      try {
        const data = {
          email,
          password,
        };
        const loginEndpoint = orgId ? endpoints.auth.loginOrg.replace('orgId', orgId) : endpoints.auth.login;
        const response = await axios.post(loginEndpoint, data);

        if (response.data.success) {
          const { user, organization } = response.data.body;
          const token = response.headers.get(`hireintel-token`);

          setSession(token);
          sendIdentify(user);

          localStorage.setItem(USER_KEY, JSON.stringify(user));
          localStorage.setItem(ORG_KEY, JSON.stringify(organization));
          var verified=true;

          if(organization && organization.approval_status === 'PENDING_APPROVAL'){
            verified=false;
          }

          dispatch({
            type: 'LOGIN',
            payload: {
              user,
              organization,
              verified,
            },
          });
          
          if(organization && user ){
              router.push(returnTo || PATH_AFTER_LOGIN);
          }
          return response.data;
        }
        throw new Error(response.message);
      } catch (error) {
        return error;
      }
    },
    [returnTo, router],
  );

  // REGISTER
  const register = useCallback(
    async (email, password, firstName, lastName) => {
      try {
        const data = {
          email,
          password,
          firstName,
          lastName,
        };

        const response = await axios.post(endpoints.auth.register, data);

        const { accessToken, user, organization } = response.data;

        localStorage.setItem(STORAGE_KEY, accessToken);

        dispatch({
          type: 'REGISTER',
          payload: {
            user,
            organization,
          },
        });

        router.push(returnTo || PATH_AFTER_LOGIN);
      } catch (error) {
        console.log(error);
      }
    },
    [returnTo, router],
  );

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);
    localStorage.setItem(USER_KEY, null);
    localStorage.setItem(ORG_KEY, null);

    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      organization: state.organization,
      verified: state.verified,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
    }),
    [login, logout, register, state.user, state.organization, state.verified, status],
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
